import {EntityListColumn, EntityPage} from 'components';
import {
	renderArrayField,
	renderDescriptionField,
	renderVexClusters,
	renderVexesPersona,
	renderVkosPersona,
} from 'components/EntityList/ColumnRenderers';
import {LoadWrapper} from 'components/LoadWrapper';
import {ViewAuthorizer} from 'components/ViewAuthorizer';
import {KeywordsTooltipTranslationProvider} from 'features/localizedTooltips';
import React, {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {
	Keyword,
	StandardPlusPcmsCluster,
	User,
	UserRole,
	VexCluster,
} from 'types';
import {
	GetAllKeywordsQuery,
	useGetAllKeywordsQuery,
} from '../AdminSection/hooks/keywords.generated';
import {
	GetAllVexClustersQuery,
	useGetAllVexClustersQuery,
} from '../AdminSection/hooks/vexClusters.generated';
import {
	DetailsRowCompat as DetailsRow,
	IDetailsRowBaseProps,
	IDetailsRowProps,
	MessageBarCompat as MessageBar,
	StackCompat as Stack,
	ToggleCompat as Toggle,
} from 'components/FluentUICompat';
import {HeaderForPagesWithEntityPage} from 'components/HeaderForPagesWithEntityPage';
import _ from 'lodash';
import {ARRAY_TYPE} from 'components/EntityList/EntityUtils';
import {useGetAllVkosQuery} from 'features/AdminSection/hooks/vko.generated';
import {alphabeticalSort, numericalSort} from 'helpers/sortHelpers';
import {getUserDepartment} from 'components/Personas';
import {URLProzess, URLVKO, URLVEX} from './vkovexConstants';
import {useBoolean} from '@fluentui/react-hooks';
import {useUserContext} from 'authentication/UserContext';

const AdminKeywordsPage: React.FC = () => {
	const {t: tKeywords} = useTranslation('features/adminsection', {
		keyPrefix: 'AdminKeywordsPage',
	});
	const {t: tClusters} = useTranslation('features/adminsection', {
		keyPrefix: 'AdminVexClustersPage',
	});
	const {t: tVkos} = useTranslation('features/adminsection', {
		keyPrefix: 'AdminVkosPage',
	});

	const {loading: keywordsLoading, data: keywordData} =
		useGetAllKeywordsQuery();
	const {loading: vexClutersLoading, data: vexClustersData} =
		useGetAllVexClustersQuery();

	const {loading: loadingVkos, data: dataVkos} = useGetAllVkosQuery();
	const [showInactive, {toggle: toggleShowInactive}] = useBoolean(false);
	const {isAvk, isBav} = useUserContext();
	const hideRegulationClustersAndVkos = isAvk || isBav;

	const getKeywordsWVexes = useCallback(
		(
			keywordData: GetAllKeywordsQuery | undefined,
			vexClustersData: GetAllVexClustersQuery | undefined,
		) => {
			let keywordsUnsorted = keywordData?.keywords ?? [];
			if (!showInactive) {
				keywordsUnsorted = keywordsUnsorted.filter(kw => kw.isActive);
			}
			const keywordsSorted = numericalSort(keywordsUnsorted, 'sortPriority');

			const mapVexCluster = new Map<string, number>();
			const vexClusters = vexClustersData?.vexClusters ?? [];
			vexClusters.forEach((x, index) => mapVexCluster.set(x.id, index));

			const vkos = dataVkos?.vkos ?? [];

			return keywordsSorted.map((keyword: Keyword) => {
				const keywordVCs: VexCluster[] = [];
				const vexesAll: User[] = [];

				keyword.vexClusters.forEach((vc: VexCluster) => {
					const currentClusterId = mapVexCluster.get(vc.id);
					if (currentClusterId !== undefined) {
						const currentCluster = vexClusters.at(currentClusterId);
						keywordVCs.push(currentCluster as VexCluster);
						currentCluster?.users?.forEach(user => {
							vexesAll.push(user);
						});
					}
				});

				const vexes = _.uniqBy(vexesAll, 'id').sort((a, b) => {
					const departA = getUserDepartment(a as User) ?? 'z';
					const departB = getUserDepartment(b as User) ?? 'z';

					return departA === departB
						? a.name.localeCompare(b.name)
						: departA.localeCompare(departB);
				});

				const keywordVkoUserIds = new Set(keyword.vkos.map((u: User) => u.id));
				const standardPlusPcmsClusters: StandardPlusPcmsCluster[] = _.uniqBy(
					vkos
						.filter(vko => keywordVkoUserIds.has(vko.vkoUser.id))
						.flatMap(
							vko => vko.standardPlusPcmsClusters as StandardPlusPcmsCluster[],
						),
					'id',
				);

				return {
					...keyword,
					vexClusters: alphabeticalSort(keywordVCs, 'name'),
					vexes,
					standardPlusPcmsClusters: alphabeticalSort(
						standardPlusPcmsClusters,
						'name',
					),
				};
			});
		},
		[showInactive, dataVkos],
	);

	const keywords: Keyword[] = useMemo(
		() =>
			vexClutersLoading || keywordsLoading || loadingVkos
				? []
				: getKeywordsWVexes(keywordData, vexClustersData),
		[
			vexClutersLoading,
			keywordsLoading,
			loadingVkos,
			getKeywordsWVexes,
			keywordData,
			vexClustersData,
			showInactive,
		],
	);

	const onRenderRow = useCallback((props?: IDetailsRowProps) => {
		if (!props || !props.columns) {
			return null;
		}
		const customStyles = {
			root: {
				backgroundColor:
					props?.item?.isActive === false ? '#FFCCCC' : undefined,
			},
		};
		return <DetailsRow {...props} styles={customStyles} />;
	}, []);

	const columns: EntityListColumn[] = useMemo(
		() => [
			{
				key: 'name',
				name: tKeywords('Keywords'),
				fieldName: 'name',
				minWidth: 150,
				maxWidth: 200,
				isResizable: true,
				filterable: true,
				sortable: true,
				filterOnFilter: true,
			},
			...(hideRegulationClustersAndVkos
				? []
				: [
						{
							key: 'regulationClusters',
							name: tKeywords('RegulationClusters'),
							fieldName: 'regulationClusters',
							minWidth: 150,
							maxWidth: 200,
							isResizable: true,
							filterable: true,
							sortable: true,
							dataType: ARRAY_TYPE,
							onRender: renderArrayField(),
						},
						{
							key: 'vkos',
							name: tKeywords('Vkos'),
							fieldName: 'vkos',
							minWidth: 200,
							maxWidth: 200,
							filterable: true,
							sortable: true,
							dataType: ARRAY_TYPE,
							onRender: renderVkosPersona(keywords, dataVkos),
							filterOnFilter: true,
						},
				  ]),
			{
				key: 'descriptions',
				name: tKeywords('Descriptions'),
				fieldName: 'descriptions',
				minWidth: 150,
				maxWidth: 200,
				isResizable: true,
				isMultiline: true,
				onRender: renderDescriptionField(),
			},
			{
				key: 'vexClusters',
				name: tKeywords('VexClusters'),
				fieldName: 'vexClusters',
				minWidth: 150,
				maxWidth: 200,
				isResizable: true,
				filterable: true,
				sortable: true,
				dataType: ARRAY_TYPE,
				onRender: renderVexClusters(),
				filterOnFilter: true,
			},
			{
				key: 'vexes',
				name: tClusters('Vexes'),
				fieldName: 'vexes',
				minWidth: 200,
				maxWidth: 200,
				filterable: true,
				sortable: true,
				dataType: ARRAY_TYPE,
				onRender: renderVexesPersona(keywords),
				filterOnFilter: true,
			},
			{
				key: 'standardPlusPcmsClusters',
				name: tVkos('PcmsClusters'),
				fieldName: 'standardPlusPcmsClusters',
				minWidth: 150,
				maxWidth: 200,
				isResizable: true,
				filterable: true,
				sortable: true,
				dataType: ARRAY_TYPE,
				onRender: renderArrayField(),
				filterOnFilter: true,
			},
		],
		[keywords, dataVkos, hideRegulationClustersAndVkos, tVkos],
	);

	const strMessageHTML = tKeywords('VkoVexMessage', {
		URLProzess,
		URLVKO,
		URLVEX,
	});
	return (
		<ViewAuthorizer
			roles={[
				UserRole.SystemAdministrator,
				UserRole.Vko,
				UserRole.Vex,
				UserRole.VexSupport,
				UserRole.Readers,
				UserRole.Avk,
				UserRole.Bav,
			]}
		>
			<HeaderForPagesWithEntityPage>
				<Stack
					horizontal
					horizontalAlign='space-between'
					styles={{root: {marginRight: 48}}}
				>
					<Stack.Item>
						<h3>{tClusters('VkoVexList')}</h3>
					</Stack.Item>
					<Stack.Item>
						<Toggle
							label={tKeywords('ShowIsActive')}
							inlineLabel
							defaultChecked={showInactive}
							onChange={toggleShowInactive}
							styles={{root: {flexDirection: 'row-reverse', gap: '8px'}}}
						/>
					</Stack.Item>
				</Stack>
			</HeaderForPagesWithEntityPage>
			<MessageBar isMultiline={true}>
				<div dangerouslySetInnerHTML={{__html: strMessageHTML}} />
			</MessageBar>
			<LoadWrapper
				loading={keywordsLoading || vexClutersLoading || loadingVkos}
			>
				<KeywordsTooltipTranslationProvider>
					<EntityPage
						items={keywords as any}
						entityDisplayName={tKeywords('Keywords')}
						createEntity={() => null}
						updateEntity={() => null}
						deleteEntity={() => null}
						withNameColumn={false}
						hideCreate={true}
						hideEdit={true}
						hideDelete={true}
						additionalColumns={columns}
						shouldWrapListWithScrollablePane={false}
						entityListProps={{onRenderRow}}
					/>
				</KeywordsTooltipTranslationProvider>
			</LoadWrapper>
		</ViewAuthorizer>
	);
};

export default AdminKeywordsPage;
